import { Fragment } from 'react'
import { Divider, ListItem, SvgIconTypeMap, Typography } from '@mui/material'
import { OverridableComponent } from '@mui/material/OverridableComponent'
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined'
import StarBorderOutlinedIcon from '@mui/icons-material/StarBorderOutlined'

import { Account } from '@/domain/usecases'
import { EnrollmentCategory } from '@/domain/models/content/enrollment-category'
import { i18nTranslationHelper } from '@/i18n'
import PathConstants from '@/main/routes/paths'
import { AccountWithCustomProperties } from '@/presentation/contexts/auth'

import ProfileAssessmentButton from '../../../button/profile-assessment-button'
import { MenuItemCollapsible } from './menu-item-collapsible'
import { MenuItemUnified } from './menu-item-unified'
import { MenuRenderer } from './menu-renderer'
import { buildNavConfig } from './nav-config'
import { FeatureFlagName, useFeatureFlags } from '@/infra/feature-flag'

type MenuSectionWrapperProps = {
  account: AccountWithCustomProperties
  variant: 'desktop' | 'mobile'
  handleClose: () => void
}

export function MenuList({ variant = 'desktop', account, handleClose }: MenuSectionWrapperProps) {
  const t = i18nTranslationHelper.getTComponents

  const { isProfileAssessmentEnabled, isUserSpaceEnabled, isLearningPreferencesEnabled } =
    useFeatureFlags({
      isProfileAssessmentEnabled: FeatureFlagName.COMPANIES_WITH_PROFILE_ASSESSMENT,
      isUserSpaceEnabled: FeatureFlagName.USER_SPACE_ENABLED,
      isLearningPreferencesEnabled: FeatureFlagName.ENABLE_USER_LEARNING_PREFERENCES
    })

  const roles = Account.getRoles(account)
  const nav = buildNavConfig(account, roles, {
    isUserSpaceEnabled,
    isLearningPreferencesEnabled
  })

  // Navigation categories (only for mobile)
  const restrictions = account.company.contentRestrictions.categories
  const enrollmentCategoryList = EnrollmentCategory.getAvailables(restrictions)

  return (
    <Fragment>
      {nav.home && <MenuRenderer variant={variant} section={nav.home} handleClose={handleClose} />}

      {nav.helpCenter && (
        <MenuRenderer variant={variant} section={nav.helpCenter} handleClose={handleClose} />
      )}

      {nav.profile && (
        <MenuRenderer variant={variant} section={nav.profile} handleClose={handleClose} />
      )}

      {roles.hasBenefitMenu && (
        <Fragment>
          {/* Descubra seu perfil — benefit users only */}
          {isProfileAssessmentEnabled && <ProfileAssessmentButton mode="mobile" />}

          {/* Mobile: content category navigation */}
          {variant === 'mobile' ? (
            <Fragment>
              <Divider component="li" sx={{ my: 0.5 }} />
              <ListItem>
                <Typography variant="caption" color="common.black">
                  {t('navMenu.mobile.listHeader')}
                </Typography>
              </ListItem>

              {enrollmentCategoryList.map(item =>
                item.hasMultipleCategories ? (
                  <MenuItemCollapsible
                    key={item.name}
                    variant="mobile"
                    icon={
                      (item.icon || HomeOutlinedIcon) as OverridableComponent<
                        SvgIconTypeMap<object, 'svg'>
                      > & { muiName: string }
                    }
                    title={item.title}
                  >
                    {item.categories.map(c => (
                      <MenuItemUnified
                        key={c.name}
                        variant="mobile"
                        route={PathConstants.findRouteByCategory(c)}
                        icon={
                          (c.icon || HomeOutlinedIcon) as OverridableComponent<
                            SvgIconTypeMap<object, 'svg'>
                          > & { muiName: string }
                        }
                        onClose={handleClose}
                      />
                    ))}
                  </MenuItemCollapsible>
                ) : (
                  <MenuItemUnified
                    key={item.name}
                    variant="mobile"
                    route={{
                      value: PathConstants.getCategoryRoutePath(item.categories[0]),
                      title: item.title,
                      name: item.name
                    }}
                    icon={
                      (item.icon || HomeOutlinedIcon) as OverridableComponent<
                        SvgIconTypeMap<object, 'svg'>
                      > & { muiName: string }
                    }
                    onClose={handleClose}
                  />
                )
              )}

              {/* Institutions */}
              <MenuItemUnified
                variant="mobile"
                route={PathConstants.Routes.INSTITUTIONS}
                icon={StarBorderOutlinedIcon}
                onClose={handleClose}
              />
            </Fragment>
          ) : null}
        </Fragment>
      )}

      {nav.team && <MenuRenderer variant={variant} section={nav.team} handleClose={handleClose} />}

      {nav.manager && (
        <MenuRenderer variant={variant} section={nav.manager} handleClose={handleClose} />
      )}

      <MenuRenderer variant={variant} section={nav.logout} handleClose={handleClose} />
    </Fragment>
  )
}
